import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../../hooks';
import styles from './FloatingActionButton.module.css';

/**
 * FloatingActionButton Component
 * 
 * A floating action button that appears when the user scrolls down.
 * Provides quick access to the download section with tooltip feedback.
 * 
 * Features:
 * - Appears on scroll (after 300px)
 * - Smooth scroll to download section
 * - Tooltip with localized text
 * - Pulse animation for attention
 * - CSS Modules for scoped styling
 * - Full accessibility support
 * - Responsive design
 * 
 * @component
 * @example
 * return (
 *   <FloatingActionButton />
 * )
 */
const FloatingActionButton = () => {
  const { language } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show FAB when user scrolls down 300px
      const scrolled = window.scrollY > 300;
      setIsVisible(scrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToDownload = (retries = 5) => {
    const downloadSection = document.getElementById('download');
    if (downloadSection) {
      // Wait for any layout shifts to complete
      requestAnimationFrame(() => {
        const yOffset = -80;
        const rect = downloadSection.getBoundingClientRect();
        const y = rect.top + window.pageYOffset + yOffset;
        
        // Check if the element seems properly positioned and has content
        if (rect.height > 50 && rect.width > 0 && rect.top >= 0) {
          window.scrollTo({ top: y, behavior: 'smooth' });
        } else if (retries > 0) {
          // Retry if the element isn't properly positioned yet
          setTimeout(() => scrollToDownload(retries - 1), 150);
        }
      });
    } else if (retries > 0) {
      // Element doesn't exist yet, retry
      setTimeout(() => scrollToDownload(retries - 1), 150);
    }
  };

  const handleClick = () => {
    // Check if we're on the home page
    if (location.pathname !== '/') {
      // Navigate to home page and then scroll to download section
      navigate('/');
      // Wait longer for React components to mount and render
      setTimeout(() => {
        scrollToDownload();
      }, 500);
    } else {
      // We're already on the home page, just scroll to download section
      scrollToDownload();
    }
  };

  const handleKeyDown = (event) => {
    // Handle Enter and Space key activation
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  if (!isVisible) return null;

  return (
    <div className={styles.tooltipContainer}>
      <button 
        className={`${styles.fab} ${styles.pulse}`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        aria-label={language === 'ar' ? 'تحميل التطبيق' : 'Download App'}
        title={language === 'ar' ? 'تحميل التطبيق' : 'Download App'}
        type="button"
      >
        <svg 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none"
          aria-hidden="true"
          className={styles.icon}
        >
          <path 
            d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
        </svg>
      </button>
      
      {showTooltip && (
        <div 
          className={styles.tooltip}
          role="tooltip"
          aria-hidden="true"
        >
          {language === 'ar' ? 'تحميل التطبيق' : 'Download App'}
        </div>
      )}
    </div>
  );
};

export default FloatingActionButton;